// ContextDropdown — the house right-click context menu, promoted from the
// /files context menu so every surface reuses its EXACT shape + styles
// (MenuPanel / MenuPanelItem). One instance is mounted globally in the app
// shell; only one menu is open at a time.
//
// Driven by `root.contextDropdown`:
//   { open, x, y, items?, itemsFn?, actionFn, openKey?, ...ctx }
//   - x / y       : viewport coords of the cursor (or the anchor button).
//   - items       : a static [{ label, action, danger?, disabled?, caption?,
//                   color?, ... }] array (simple menus — e.g. chat
//                   channel/DM/group rows).
//   - itemsFn     : a registered fn name; `el.call(itemsFn, root)` returns the
//                   array. Use this when the rows are derived from state and
//                   can change WITHOUT reopening — e.g. the files multi-step
//                   "Move to folder…" picker.
//   - actionFn    : a registered fn name; on a row click the menu calls
//                   `el.call(actionFn, action, item)`. The handler OWNS the
//                   dispatch AND the close: terminal actions clear
//                   `root.contextDropdown`; step actions re-set it (stay open).
//   - openKey     : the `key` of whichever row's SUBMENU is currently open
//                   (see below) — owned entirely by this component, never
//                   set by an opener.
//   - ...ctx      : any extra the openers/handlers need (nodeId, channelId…).
//
// ── Row states — the same three flags on every row shape, static `items`
// and `itemsFn` alike ───────────────────────────────────────────────────
//   - danger: true       the destructive row: MenuPanelItem's `dangerInk` + that
//                        ink's own hover/press washes (the legacy `color: 'red'`
//                        means the same). It was the raw brand `red`.
//   - disabled: true     the row is INERT but stays ON SCREEN and stays
//                        REACHABLE. `aria-disabled="true"`, no hover/press
//                        ground, no `actionFn` dispatch from click / Enter /
//                        Space, and no submenu opened by hover, click or
//                        ArrowRight. Ink drops to the house `inactive` token —
//                        NOT an opacity fade, which would push the row under
//                        the house 4.5:1 floor and dim its icon with it.
//                        Deliberately NOT the native `disabled` ATTRIBUTE:
//                        `.focus()` is a no-op on a disabled <button>, so the
//                        row would fall out of `menuLevelNav`'s arrow cycle
//                        and strand it. WAI-ARIA APG keeps a disabled menu
//                        item focusable and inert instead, which is what this
//                        does. `disabled` BEATS `danger` — a row that cannot
//                        fire must not advertise a destructive one.
//   - caption: '<key>'   a SECOND line under the label, `caption` ink at `Y`,
//                        resolved through polyglot exactly like `label` (a
//                        plain string passes through untouched). Absent →
//                        nothing renders and the row keeps its one-line
//                        height. The line lives INSIDE the row, so it joins
//                        the row's accessible name from content the same way
//                        `label` already does — no `aria-describedby` id
//                        plumbing, which no menu in this house has ever used.
//                        Pairs with `disabled` to say WHY a row is inert
//                        ("Collapse" / "Nothing to fold") instead of folding
//                        the reason into the label and dispatching a no-op.
//   - checked: bool      a current-value row: a `menuitemcheckbox` with
//                        aria-checked and the house check glyph at the far end
//                        (its box is kept when unchecked, so labels align). A
//                        row whose `action` is `{ multi, field, value }` (the
//                        tickets people pickers) derives it LIVE from
//                        root[field]. Absent → a plain `menuitem`, no box.
//   - shortcut: ['⌘','C'] trailing AppKeycap caps (a string is one cap) — ONLY
//                        for a shortcut that is wired; pair it with
//                        `ariaKeyshortcuts: 'Meta+C'` so AT hears it. The caps
//                        lift on the highlighted row (MenuPanelItem's rule).
//
// The transparent full-viewport backdrop closes the menu on any outside click
// or right-click. Position is viewport-clamped so it never overflows.
//
// Nested rows (2nd level): give any row a `children: [{ key, action, label,
// … }, …]` array (same shape as a top-level row) and it renders as a
// disclosure row (chevron, no `action` of its own) that reveals a flyout
// submenu of those rows — hover (120ms open / 250ms close intent, matching
// @symbo.ls/dropdown's DropdownTrigger), click, and keyboard (ArrowRight /
// Enter opens + focuses the first row, ArrowLeft / Escape closes + returns
// focus to the parent row) all drive the SAME `openKey` so the three
// modalities can never disagree. The flyout is `position: fixed` and
// positioned via the dropdown plugin's own `computePosition` (viewport-edge
// flip, right→left) — that also means it is never clipped by MenuPanel's
// `overflow: auto`, with no need to reach in and override an ancestor's
// overflow. Only one level of nesting ships today (this is what the project
// canvas "New" row needs); a deeper level is a mechanical repeat of the same
// recipe since `Submenu` rows are themselves `ContextDropdownItem`s.

// Reuse the dropdown plugin's own viewport-edge-flip math for the submenu
// flyout instead of hand-rolling a second one — same package
// shared/functions/index.js already imports for openDropdown/closeDropdown.
import { computePosition } from '@symbo.ls/dropdown'

export const ContextDropdownItem = {
  // FA204 / frank trap — every helper below lives in `scope`, not module
  // scope: a bare module-scope const/function silently reads as undefined
  // once this component is frank.toJSON'd for platform delivery (workspace
  // d6ea1142, project_frank_drops_module_local_helpers). Called via
  // `el.scope.<name>(...)` from any descendant factory/handler. Regular
  // method syntax (not arrows) throughout — `el.scope.foo()` binds `this`
  // to `el.scope`, so a sibling helper calling another does it via
  // `this.<name>(...)` (object-literal properties can't see each other as
  // bare identifiers otherwise). `menuLevelNav` is ALSO needed by the
  // sibling `ContextDropdown` export below (its top-level Menu.onKeydown) —
  // `el.scope` only resolves within the tree of the specific top-level
  // export it's defined on, so it's duplicated there rather than shared.
  scope: {
    // ── nested rows (2nd level) ──────────────────────────────────────────
    //
    // A row is a "parent" row when its data carries a non-empty `children`
    // array (same shape as top-level rows). `openKey` — the currently-open
    // parent row's `key`, or falsy for none — lives on `root.contextDropdown`
    // itself, a SIBLING of `open`/`x`/`y`/…, NOT on the row's own data: every
    // itemsFn rebuilds its rows fresh on each contextDropdown change, so
    // anything stored on the row objects would be wiped the instant openKey
    // changes. Keeping it on the shared parent object survives that rebuild
    // and self-resets for free — every existing opener already replaces the
    // WHOLE `contextDropdown` object on open (never merges), so a
    // freshly-opened menu never inherits a stale openKey from a previous
    // session.
    hasSub(s) {
      return !!(s && Array.isArray(s.children) && s.children.length)
    },

    // A row's checked state: `undefined` = not a checkable row (a plain
    // menuitem, no check box); true / false = a menuitemcheckbox. A
    // multi-select row (`action: { multi, field, value }`) derives it LIVE
    // from the bound root filter field (row state snapshots go stale in
    // v3.14 childrenAs); any other row reads its own `checked`.
    checkedOf(s) {
      const a = s && s.action
      if (a && a.multi && a.field) {
        const picked = [].concat((s.root && s.root[a.field]) || []).filter(Boolean)
        return a.value ? picked.includes(a.value) : !picked.length
      }
      return s && s.checked !== undefined ? !!s.checked : undefined
    },

    // The row's keycaps: `shortcut: ['⌘', 'C']` or a one-cap string.
    keysOf(s) {
      const k = s && s.shortcut
      if (Array.isArray(k)) return k.filter((c) => c != null && c !== '').map(String)
      return k ? [String(k)] : []
    },

    // Arrow-key navigation WITHIN one menu level (the top Menu or a
    // Submenu) — same keys/wrap-around math as CanvasNavbar's CreateMenu
    // keyboard model, generalized to whichever container the key bubbled
    // through. `:scope >` scopes to the container's OWN direct rows so a
    // nested Submenu's items never fold into a containing Menu's up/down
    // cycle (and vice-versa); stopPropagation once a key is actually
    // handled so an ancestor level can't ALSO react to the same keystroke.
    menuLevelNav(e, containerNode) {
      if (!containerNode) return
      const items = Array.from(
        containerNode.querySelectorAll(
          ':scope > [role="menuitem"], :scope > [role="menuitemcheckbox"]'
        )
      )
      if (!items.length) return
      const idx = items.indexOf(document.activeElement)
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        e.stopPropagation()
        // idx < 0 (nothing in this level focused yet) lands on the first row —
        // NOT `(idx + 1) % length` with idx = -1, which would land on the
        // second-to-last row instead of a sane starting point.
        items[idx < 0 ? 0 : (idx + 1) % items.length].focus()
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        e.stopPropagation()
        items[idx < 0 ? items.length - 1 : (idx - 1 + items.length) % items.length].focus()
      } else if (e.key === 'Home') {
        e.preventDefault()
        e.stopPropagation()
        items[0].focus()
      } else if (e.key === 'End') {
        e.preventDefault()
        e.stopPropagation()
        items[items.length - 1].focus()
      }
    },

    // Open/close a row's submenu — the SINGLE place that writes `openKey`,
    // used by hover, click AND keyboard alike so all three trigger
    // modalities agree on one source of truth (no CSS-hover-vs-JS-state
    // fights, no `!important` cascade tie-breaks). `opts.focusFirst` is
    // keyboard-open ONLY (ArrowRight / Enter): a passing mouse hover — or
    // even a click, which already parks native focus on the row itself —
    // must never silently steal keyboard focus into the flyout.
    openSubmenu(el, s, opts) {
      const dd = s && s.root && s.root.contextDropdown
      if (!dd) return
      s.root.update({ contextDropdown: { ...dd, openKey: s.key } }, { preventFetch: true })
      if (typeof window === 'undefined' || !opts || !opts.focusFirst) return
      // The submenu's rows don't exist in the DOM until this state commits
      // and the (previously display:none) panel paints — poll a few
      // bounded ticks rather than assume one microtask is enough (same
      // reasoning as subSettle below).
      let tries = 0
      const focusFirst = () => {
        const first =
          el.Submenu &&
          el.Submenu.node &&
          el.Submenu.node.querySelector('[role="menuitem"], [role="menuitemcheckbox"]')
        if (first) first.focus()
        else if (++tries < 6) window.setTimeout(focusFirst, 30)
      }
      window.setTimeout(focusFirst, 0)
    },

    closeSubmenu(el, s, opts) {
      const dd = s && s.root && s.root.contextDropdown
      if (!dd) return
      s.root.update({ contextDropdown: { ...dd, openKey: null } }, { preventFetch: true })
      if (opts && opts.refocus && el && el.node && el.node.focus) el.node.focus()
    },

    // Position an open submenu against its OWNER ROW using the SAME
    // viewport-flip math the top-level dropdown plugin panels use
    // (@symbo.ls/dropdown's computePosition: 'right-start', flips to
    // 'left-start' when the right side would clip). `position: fixed` — not
    // `absolute` — so the panel is positioned against the viewport rather
    // than MenuPanel's own `overflow: auto` box; fixed-position descendants
    // aren't clipped by an ancestor's overflow at all, so there's no need
    // to reach in and flip MenuPanel's overflow to visible the way
    // CascadeMenu's flyout does.
    subPos(el, s) {
      const rowNode = el && el.parent && el.parent.node
      if (typeof window === 'undefined' || !rowNode) return { left: 0, top: 0 }
      const r = rowNode.getBoundingClientRect()
      const anchor = {
        left: r.left,
        top: r.top,
        right: r.right,
        bottom: r.bottom,
        width: r.width,
        height: r.height
      }
      const size = {
        width: (el._subSize && el._subSize.w) || 240,
        height: (el._subSize && el._subSize.h) || Math.round(window.innerHeight * 0.6)
      }
      return computePosition(anchor, size, 'right-start')
    },

    // Re-measure the submenu's REAL rendered size and re-apply the computed
    // position — mirrors ddSettle on the ContextDropdown export below
    // (onRender fires before the rows lay out, measuring 0×0; a short
    // bounded poll catches the real size once painted). Only runs while
    // THIS row's submenu is the open one.
    subSettle(el) {
      if (typeof window === 'undefined' || !el || !el.parent) return
      el.__subSettleGen = (el.__subSettleGen || 0) + 1
      const gen = el.__subSettleGen
      let ticks = 0
      const tick = () => {
        if (gen !== el.__subSettleGen) return
        const dd = el.state?.root?.contextDropdown
        const ownerKey = el.state?.key
        if (!dd || dd.openKey !== ownerKey || !el.node) return
        const w = el.node.offsetWidth
        const h = el.node.offsetHeight
        if (w > 0 && h > 0) el._subSize = { w, h }
        const pos = this.subPos(el, el.state)
        el.node.style.left = pos.left + 'px'
        el.node.style.top = pos.top + 'px'
        if (++ticks < 24) window.setTimeout(tick, 50)
      }
      tick()
    }
  },
  // Same row chrome as the /files context-menu item (MenuPanelItem already
  // carries the house hover styles). ⚠ 'MenuPanelItem', the ROW — not
  // 'MenuPanel', the panel: the dd72ccfd frank-trap sweep accidentally
  // rewrote this to the panel and every context-menu row lost its hover
  // wash + gained panel chrome (Nika 2026-08-12, "fix ui of context menus
  // (hover states etc)").
  extends: 'MenuPanelItem',
  flow: 'x',
  align: 'center left',
  gap: 'Z1',
  // THE ROW STATES ride MenuPanelItem's recipe (white-label redesign
  // 2026-09-26, DESIGN_SPEC §5.9): the row keeps the recipe's static `title`
  // ink, and each state is an ATTRIBUTE the recipe's static rules key on —
  // never an inline colour factory (a factory-valued `color` is written
  // inline and outranks every class rule, so the recipe's danger ink could
  // never land):
  //   - `data-danger` (danger, or the legacy `color: 'red'`) → `dangerInk` +
  //     the ink's own hover/press washes from MenuPanelItem. It was the raw
  //     brand `red`, ~2.4:1 as text on the light panel.
  //   - `aria-disabled="true"` → the house `inactive` ink, a not-allowed
  //     cursor and no hover/press ground. The ink/cursor rule outranks the
  //     recipe's flat classes; the ground rules TIE the recipe's doubled-class
  //     `:hover`/`:active` on specificity and win by order — a row mints its
  //     merged keys in order, MenuPanelItem's first, and nothing else mints
  //     these three rules (contextDropdownRecipe.test.js pins the order).
  //     `disabled` is checked FIRST — an inert row must not advertise a
  //     destructive action it cannot perform (see the row-state contract at
  //     the top of this file).
  // EVERY factory below reads through `s?.` — the v3.14 children machinery
  // instantiates the childExtends TEMPLATE once with NO state bound (an
  // empty-items open does exactly that), and ONE throw inside the children
  // effect kills the subscription for good: every context menu in the app
  // then opens EMPTY until reload (2026-08-06 per Nika "context menu on
  // dashboard got broke - no items in it" — `s.divider` on undefined threw
  // here). Framework ticket in tickets/smbls.md covers the template-eval +
  // dead-effect root cause; the guards are the component's own contract.
  dataDanger: (el, s) => !s?.disabled && !!(s?.danger || s?.color === 'red'),
  ariaDisabled: (el, s) => (s?.disabled ? 'true' : null),
  // The house `inactive` ink, not an `opacity` fade. An alpha fade multiplies
  // through to the row's Icon/check/Chevron as well and lands the label under
  // the house 4.5:1 floor; `inactive` is a measured adaptive pair
  // (gray6 / gray10) that clears it on both themes. `not-allowed` is the
  // affordance the `field` theme's `:disabled` block and AiMenu's AiOptionRow
  // already use for an inert control.
  '&[aria-disabled="true"]': { color: 'inactive', cursor: 'not-allowed' },
  '&[aria-disabled="true"]:hover': { background: 'transparent' },
  '&[aria-disabled="true"]:active': { background: 'transparent' },
  // A `{ divider: true }` row renders as a non-interactive separator line
  // (the tickets people pickers use it to split humans from agents —
  // 2026-08-06 per Nika "in filter drdopwon make line and list agents
  // below humans"). role 'separator' keeps it out of the arrow-key cycle
  // (menuLevelNav queries [role="menuitem"] only) and out of the
  // accessibility action list; every visual part of the row collapses via
  // the divider gates below.
  // A row with a checked state (`checked`, or a multi-select action) is a
  // menuitemcheckbox — the arrow cycle and focus-first query both roles.
  role: (el, s) => {
    if (s?.divider) return 'separator'
    return el.scope.checkedOf(s) === undefined ? 'menuitem' : 'menuitemcheckbox'
  },
  ariaChecked: (el, s) => {
    if (s?.divider) return null
    const c = el.scope.checkedOf(s)
    return c === undefined ? null : c ? 'true' : 'false'
  },
  ariaKeyshortcuts: (el, s) => s?.ariaKeyshortcuts || null,
  // A row with `children` is a disclosure row (opens a Submenu, no action of
  // its own) — rendered as a <div>, NOT a <button>: its flyout holds MORE
  // buttons, and button-in-button is invalid HTML (the browser silently
  // drops the nested ones). Leaf (action) rows stay <button>s, unchanged
  // from before this existed — MenuPanelItem's own `tag`/`type` already
  // default to 'button', so every existing flat-menu consumer (files, chat,
  // tickets, home, …) renders exactly as it did prior to this.
  tag: (el, s) => (el.scope.hasSub(s) || s?.divider ? 'div' : 'button'),
  type: (el, s) => (el.scope.hasSub(s) || s?.divider ? null : 'button'),
  tabindex: (el, s) => (el.scope.hasSub(s) ? '0' : null),
  // Conditional class, NOT null-returning prop factories: a factory that
  // returns null for the common case would still override the
  // MenuPanelItem base padding/hover on every NORMAL row. The class only
  // exists on divider rows.
  isDivider: (el, s) => !!s?.divider,
  '.isDivider': {
    pointerEvents: 'none',
    minHeight: '0',
    height: '1px',
    padding: '0',
    margin: 'Y -Z',
    background: 'line'
  },
  ariaHaspopup: (el, s) => (el.scope.hasSub(s) ? 'true' : null),
  ariaExpanded: (el, s) => {
    if (!el.scope.hasSub(s)) return null
    const dd = s.root && s.root.contextDropdown
    return dd && dd.openKey === s.key ? 'true' : 'false'
  },
  // Optional leading avatar (people pickers) or icon. ADDITIVE: rows that
  // pass neither render label-only, identical to before — the label just
  // moved from the button's `text` into a Label child span so an avatar can
  // sit beside it.
  Avatar: {
    extends: 'Avatar',
    // `if:` first: a row with no avatar mounts NO Avatar element at all, so
    // no CSS pass can ever show one (2026-10-27 per Nika "dropdown has bug in
    // list list items show avatar sometimes" — the Home · Ask · Create menu).
    // The Icon slot below already ships this gate; `if:` is live since
    // element 3.14.788. A row's `avatar` is fixed for the life of the row
    // (the items feed rebuilds the list), so the gate never shifts layout.
    if: (el, s) => !!s?.avatar,
    // `hide:` (registered CSS prop) stays as the fallback for a runtime whose
    // `if:` is inert (element builds before 3.14.788): there every plain row
    // (home Refresh/Customize, …) rendered a stray empty-src initials avatar
    // (2026-07-25 per Nika "remove that avatar from / page contextmenu").
    hide: (el, s) => !s?.avatar,
    // AND an explicit display factory: published element builds up to
    // 3.14.779 carry FW-HIDE-UNAPPLIED-1 (a hide factory true from its
    // first run is clobbered by the explicit-display pass — fixed in smbls
    // 7031e3d60, unpublished while npm is billing-locked), which re-showed
    // this avatar (2026-08-18 per Nika, "persists"). The explicit display
    // IS the pass that wins on every runtime, old and fixed. This slot has
    // now been broken by two different framework regressions — keep both
    // gates.
    display: (el, s) => (s?.avatar ? null : 'none'),
    width: 'A',
    height: 'A',
    minWidth: 'A',
    // No `|| ''` — an empty `src` is a REAL request for the document URL
    // (4 failed requests per mount on the served catalog, 2026-09-20).
    // smbls removes the attribute when a factory answers undefined, which
    // is the honest "this row has no avatar" state; the two gates above
    // still hide the box. See __tests__/emptyImageSrcShared.test.js.
    src: (el, s) => s?.avatar || undefined
  },
  // Optional leading glyph. `if:` — NOT `hide:` alone — because a HIDDEN Icon
  // is still a MOUNTED Icon, and the built-in resolves its name from
  // `el.name || el.icon || el.key` (default-config Icon/index.js
  // `getIconName`): a row that names no glyph answered `undefined` from the
  // factory, fell through to the KEY, and warned
  // `[Icon] Unknown icon name "Icon" at <…ContextDropdown.Menu.0.Icon>` on
  // EVERY page while painting the `noIcon` placeholder behind
  // `display: none`. `if:` is the house recipe for exactly this slot —
  // AiMenu's AiOptionRow ships `Icon: { if: (el, s) => !!s.icon, … }`.
  // No layout shift (the RULES caution on `if:`-gated chrome): a row's
  // `icon` is fixed for the life of that row — the items feed rebuilds the
  // whole list, it never grows a glyph under a mounted row.
  // The Avatar slot above carries the same three gates. Here the gate is
  // additive — `hide:` stays
  // as the fallback for a runtime whose `if:` is inert (published element
  // builds before 3.14.788), so an old runtime still hides the box exactly
  // as it does today.
  Icon: {
    extends: 'Icon',
    if: (el, s) => !!s?.icon && !s?.avatar,
    hide: (el, s) => !!s?.avatar || !s?.icon,
    icon: (el, s) => s?.icon,
    boxSize: 'A',
    minWidth: 'A',
    // The row's own `gap` spaces the glyph from the label, as it always did.
    // MenuPanelItem's glyph slot (merged in underneath) adds a `Y1` margin
    // for rows WITHOUT a gap — here it would double the space, so the
    // inherited declaration is removed (null = the tombstone).
    marginRight: null
  },
  // Label, plus the optional `caption` second line. The Body/Label/Sub column
  // is the SAME recipe AiMenu's AiOptionRow and AiPickerRow already ship for a
  // menu row with secondary text — one house shape for it, not a second one.
  // `Body` takes the `flex: '1'` the Label used to carry, so Check/Chevron
  // still get pushed to the row's trailing edge and a long label wraps
  // inside the row (`minWidth: '0'` lets the column shrink under its content).
  Body: {
    flow: 'y',
    gap: 'V',
    flex: '1',
    minWidth: '0',
    // ONLY tightened when there is a second line to tighten. The panel/theme
    // line-height a one-line row inherits today is what sets its height, and
    // the contract is that a caption-less row keeps EXACTLY the height it has
    // now — so the common case returns null (no rule, inherit) rather than a
    // value that would silently re-scale every existing context menu.
    lineHeight: (el, s) => (s?.caption ? '1.3' : null),
    // THE TEXT LAW (overflow law §3b.8): the row's primary text WRAPS — it
    // is never cut to an ellipsis (it was nowrap + hidden + ellipsis).
    Label: {
      tag: 'span',
      whiteSpace: 'normal',
      overflowWrap: 'anywhere',
      text: (el, s) => el.call('polyglot', s?.label || '')
    },
    // `Sub`, NOT `Caption`: `Caption` is a REGISTERED built-in atom
    // (@symbo.ls/default-config Atoms/Text.js — `{ tag: 'caption' }`), so a
    // bare `Caption:` key auto-extends it and emits a real HTML <caption>
    // outside any table — the exact collision MarketplaceFeedPost hit and
    // renamed away from (e0c2ee3b). `Sub` is the key AiMenu / AiPickerRow /
    // AiSettingsPanel already use for this slot and collides with nothing.
    //
    // Both gates, same reasoning as the Avatar slot above: `hide:` alone was
    // clobbered by FW-HIDE-UNAPPLIED-1 on published builds up to 3.14.779,
    // and a stray empty line here would add row height to EVERY caption-less
    // row — precisely what the contract forbids.
    Sub: {
      tag: 'span',
      hide: (el, s) => !s?.caption,
      display: (el, s) => (s?.caption ? null : 'none'),
      fontSize: 'Y',
      lineHeight: '1.4',
      // Stays `caption` ink even on a disabled row: the reason is the one
      // thing the user still needs to read, and `caption` is the token named
      // for exactly this role.
      color: 'caption',
      // Secondary meta: the one text the law lets ellipsize — with its full
      // text in `title`, so the cut part stays readable on hover.
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      title: (el, s) => el.call('polyglot', s?.caption || '') || null,
      text: (el, s) => el.call('polyglot', s?.caption || '')
    }
  },
  // MenuPanelItem's trailing slots, fed from the row DATA (they merge in
  // underneath this definition, so their DOM place is BEFORE Avatar/Body —
  // `order` puts them after the label, where the recipe draws them).
  //
  // Keycaps — `shortcut: ['⌘', 'C']`. The recipe's lift rule raises them on
  // the highlighted row.
  Shortcut: {
    order: '1',
    hide: (el, s) => !el.scope.keysOf(s).length,
    childExtends: 'AppKeycap',
    children: (el, s) => el.scope.keysOf(s).map((text) => ({ text }))
  },
  // The check — the house glyph at the far end, in the row's own ink (so an
  // inert row's check follows it into `inactive`). A checkable row keeps the
  // glyph's BOX when unchecked, so labels stay aligned down the menu; a
  // plain row has no box. Multi-select rows derive it live (checkedOf).
  Icon_check: {
    order: '1',
    hide: (el, s) => el.scope.checkedOf(s) === undefined,
    visibility: (el, s) => (el.scope.checkedOf(s) ? 'visible' : 'hidden')
  },
  // Disclosure affordance — only a `children` row shows it. Pushed to the
  // row's far edge (same `margin: 0 0 0 auto` idiom the legacy canvas
  // dropdown used for its own trailing icon).
  Chevron: {
    extends: 'Icon',
    order: '2',
    hide: (el, s) => !el.scope.hasSub(s),
    icon: 'chevronRight',
    boxSize: 'Z1',
    color: (el, s) => (s?.disabled ? 'inactive' : 'caption'),
    margin: '0 0 0 auto',
    ariaHidden: 'true'
  },
  // The flyout — only rendered/positioned for a `children` row. Recurses
  // through the SAME ContextDropdownItem for its own rows, so a future
  // deeper level is a mechanical repeat of this recipe.
  Submenu: {
    extends: 'MenuPanel',
    position: 'fixed',
    zIndex: '20',
    width: 'max-content',
    maxWidth: 'calc(100dvw - var(--spacing-B))',
    display: (el, s) => {
      if (!el.scope.hasSub(s)) return 'none'
      const dd = s.root && s.root.contextDropdown
      return dd && dd.openKey === s.key ? 'flex' : 'none'
    },
    style: {
      left: (el, s) => el.scope.subPos(el, s).left + 'px',
      top: (el, s) => el.scope.subPos(el, s).top + 'px'
    },
    subscribeTo: ['contextDropdown'],
    onRender: (el) => el.scope.subSettle(el),
    onUpdate: (el) => el.scope.subSettle(el),
    onKeydown: (e, el) => el.scope.menuLevelNav(e, el.node),
    // Bridge the hover gap between the row and its flyout — entering EITHER
    // cancels the other's pending close-intent timer (same shared-timer
    // idiom AppNavbar's More menu uses between its button and panel).
    onMouseenter: (e, el) => {
      const rowEl = el.parent
      if (rowEl && rowEl._closeTimer) {
        clearTimeout(rowEl._closeTimer)
        rowEl._closeTimer = null
      }
    },
    onMouseleave: (e, el, s) => {
      const rowEl = el.parent
      if (!rowEl) return
      rowEl._closeTimer = window.setTimeout(() => {
        const dd = s.root && s.root.contextDropdown
        if (dd && dd.openKey === s.key) el.scope.closeSubmenu(rowEl, s)
      }, 250)
    },
    childExtends: 'ContextDropdownItem',
    childrenAs: 'state',
    children: (el, s) => (s?.children || []).filter((it) => it && typeof it === 'object')
  },
  onMouseenter: (e, el, s) => {
    const dd = s.root && s.root.contextDropdown
    // Hovering any row closes a DIFFERENT row's open submenu right away —
    // only one flyout is ever open at once (matches native menu behaviour).
    // A row INSIDE a flyout is not a different row: it is the open flyout's
    // own content. Without this guard the pointer closed the flyout the
    // moment it entered one of its rows, so no flyout row could ever be
    // clicked with a mouse (measured on dev 2026-09-26: the avatar menu's
    // Theme › Light never applied — mousedown on the row, the flyout gone
    // before mouseup).
    const inFlyout = !!(el.parent && el.parent.key === 'Submenu')
    if (dd && dd.openKey && dd.openKey !== s.key && !inFlyout) {
      if (el._closeTimer) {
        clearTimeout(el._closeTimer)
        el._closeTimer = null
      }
      s.root.update({ contextDropdown: { ...dd, openKey: null } }, { preventFetch: true })
    }
    // Closing a DIFFERENT row's flyout above is menu-wide behaviour and still
    // applies when the pointer lands on an inert row; opening this row's OWN
    // flyout does not.
    if (s?.disabled || !el.scope.hasSub(s)) return
    if (el._closeTimer) {
      clearTimeout(el._closeTimer)
      el._closeTimer = null
    }
    if (el._hoverTimer) clearTimeout(el._hoverTimer)
    // Same 120ms hover-intent delay as the house DropdownTrigger
    // (@symbo.ls/dropdown) — a deliberate pause, not a flinch, before a
    // passing hover opens a flyout.
    el._hoverTimer = window.setTimeout(() => el.scope.openSubmenu(el, s), 120)
  },
  onMouseleave: (e, el, s) => {
    if (!el.scope.hasSub(s)) return
    if (el._hoverTimer) {
      clearTimeout(el._hoverTimer)
      el._hoverTimer = null
    }
    // Same 250ms close-intent delay as DropdownTrigger — lets the pointer
    // transit the gap into the flyout without it slamming shut.
    el._closeTimer = window.setTimeout(() => {
      const dd = s.root && s.root.contextDropdown
      if (dd && dd.openKey === s.key) el.scope.closeSubmenu(el, s)
    }, 250)
  },
  onKeydown: (e, el, s) => {
    const root = s.root
    if (!root || !root.contextDropdown) return
    const hasSub = el.scope.hasSub(s)

    // Inert, but still in the arrow cycle: swallow the ACTIVATION keys only
    // (Enter / Space open nothing and dispatch nothing; preventDefault also
    // stops the <button>'s native keydown->click, so onClick is never even
    // reached) and let every NAVIGATION key — Arrow up/down, Home, End,
    // Escape — bubble to the level container untouched, so the row stays
    // reachable per WAI-ARIA APG instead of trapping or skipping the walk.
    if (s?.disabled) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar' || e.key === 'ArrowRight') {
        e.preventDefault()
        e.stopPropagation()
      }
      return
    }

    if ((e.key === 'ArrowRight' || e.key === 'Enter') && hasSub && e.target === el.node) {
      e.preventDefault()
      e.stopPropagation()
      el.scope.openSubmenu(el, s, { focusFirst: true })
      return
    }

    if (e.key === 'ArrowLeft' || e.key === 'Escape') {
      if (hasSub && root.contextDropdown.openKey === s.key) {
        e.preventDefault()
        e.stopPropagation()
        el.scope.closeSubmenu(el, s, { refocus: true })
      }
      // Otherwise nothing of MINE to close — let it bubble, either to the
      // ancestor row that owns the currently-open level, or all the way to
      // the Menu container, which closes the WHOLE context menu on Escape
      // once nothing nested is left open.
    }
  },
  onClick: (e, el, s) => {
    // Inert: no submenu toggle, no `actionFn` dispatch, and the menu stays
    // open. stopPropagation is load-bearing, not defensive — a row inside a
    // Submenu is a DOM descendant of its parent row, so an unstopped click
    // would bubble into that parent's own onClick and toggle the flyout shut
    // under the user.
    if (s?.disabled) {
      e.stopPropagation()
      return
    }
    if (el.scope.hasSub(s)) {
      e.stopPropagation()
      const dd = s.root && s.root.contextDropdown
      if (!dd) return
      if (dd.openKey === s.key) el.scope.closeSubmenu(el, s)
      else el.scope.openSubmenu(el, s)
      return
    }
    const dd = s.root && s.root.contextDropdown
    if (dd && dd.actionFn) el.call(dd.actionFn, s.action, s)
  }
}

export const ContextDropdown = {
  // FA204 / frank trap (see ContextDropdownItem above for the full
  // explanation) — `menuLevelNav` is duplicated here rather than shared:
  // `el.scope` only resolves within the tree of the specific top-level
  // export it's defined on, not across sibling top-level exports in this
  // file.
  scope: {
    // Clamp a context-menu (x, y) into the viewport. The height defaults to
    // MenuPanel's 60vh maxHeight cap — the TALLEST the menu can ever be —
    // until onRender measures the real size onto `el._ddSize`. Using the
    // worst-case cap (not a small guess) means even the first, unmeasured
    // paint of a long list can't run off the bottom edge; once measured,
    // short menus hug the cursor.
    ddMenuPos(el, dd) {
      const pad = 8
      const vw = typeof window !== 'undefined' ? window.innerWidth : 1200
      const vh = typeof window !== 'undefined' ? window.innerHeight : 800
      const w = (el && el._ddSize && el._ddSize.w) || el?.node?.offsetWidth || 220
      const h = (el && el._ddSize && el._ddSize.h) || Math.round(vh * 0.6)
      // An anchor at the screen's bottom or right edge (the account menu of a
      // navbar placed at the bottom or on a rail — shared accountMenu.js) names
      // the menu's FAR edge: `fromRight` — x is its right edge, `fromBottom` —
      // y is its bottom edge, so it opens leftward / upward off the anchor.
      const x = dd.fromRight ? (dd.x || 0) - w : dd.x || 0
      const y = dd.fromBottom ? (dd.y || 0) - h : dd.y || 0
      return {
        left: Math.max(pad, Math.min(x, vw - w - pad)),
        top: Math.max(pad, Math.min(y, vh - h - pad))
      }
    },

    // Measure the menu's REAL rendered size and re-clamp — repeatedly, on a
    // short BOUNDED timer. Why a setTimeout chain and not onRender / rAF /
    // ResizeObserver: onRender runs before the rows lay out (measures 0×0)
    // and doesn't reliably re-fire per open, while rAF + ResizeObserver are
    // frame-driven and Chrome throttles frames in backgrounded/automated
    // tabs — the same reason the home Gridstack init polls on a plain timer
    // (see workspace pages/main.js). Symptom fixed here: right-click near
    // the BOTTOM of / — with no measured size the clamp assumed the 60vh
    // worst-case cap and parked the short menu ~40vh above the cursor, and
    // nothing ever corrected it. Each tick measures (zero-guarded: a hidden
    // node's 0×0 never clobbers a real measurement), caches onto
    // `el._ddSize` — which the reactive style factory reads too, so both
    // writers agree — and re-writes the clamped position. Stops when the
    // menu closes, a newer open supersedes it (gen token), or after ~1.2s
    // (late-loading avatar rows still get caught).
    ddSettle(el) {
      if (typeof window === 'undefined' || !el) return
      el.__ddSettleGen = (el.__ddSettleGen || 0) + 1
      const gen = el.__ddSettleGen
      let ticks = 0
      const tick = () => {
        if (gen !== el.__ddSettleGen) return
        const dd = el.state?.root?.contextDropdown
        if (!dd || !dd.open || !el.node) return
        const w = el.node.offsetWidth
        const h = el.node.offsetHeight
        if (w > 0 && h > 0) el._ddSize = { w, h }
        const p = this.ddMenuPos(el, dd)
        el.node.style.left = p.left + 'px'
        el.node.style.top = p.top + 'px'
        if (++ticks < 24) window.setTimeout(tick, 50)
      }
      tick()
    },

    // Arrow-key navigation WITHIN one menu level (the top Menu or a
    // Submenu) — same keys/wrap-around math as CanvasNavbar's CreateMenu
    // keyboard model, generalized to whichever container the key bubbled
    // through. `:scope >` scopes to the container's OWN direct rows so a
    // nested Submenu's items never fold into a containing Menu's up/down
    // cycle (and vice-versa); stopPropagation once a key is actually
    // handled so an ancestor level can't ALSO react to the same keystroke.
    menuLevelNav(e, containerNode) {
      if (!containerNode) return
      const items = Array.from(
        containerNode.querySelectorAll(
          ':scope > [role="menuitem"], :scope > [role="menuitemcheckbox"]'
        )
      )
      if (!items.length) return
      const idx = items.indexOf(document.activeElement)
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        e.stopPropagation()
        items[idx < 0 ? 0 : (idx + 1) % items.length].focus()
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        e.stopPropagation()
        items[idx < 0 ? items.length - 1 : (idx - 1 + items.length) % items.length].focus()
      } else if (e.key === 'Home') {
        e.preventDefault()
        e.stopPropagation()
        items[0].focus()
      } else if (e.key === 'End') {
        e.preventDefault()
        e.stopPropagation()
        items[items.length - 1].focus()
      }
    },

    // Escape-close — and every arrow-key move — only works once the
    // delegated keydown walk can reach a handler ON or inside Menu:
    // DOMQL's delegated events walk the ANCESTOR CHAIN of `event.target`,
    // starting at `event.target` itself (smbls/packages/element/src/
    // delegate.js installDelegatedEvent: `let target = event.target; while
    // (target !== rootNode) { check; target = target.parentNode }`), and
    // opening the menu never moved focus into it anywhere — so "open menu
    // -> press Escape immediately" left `event.target` wherever focus
    // already was (often `document.body`), never a descendant of (or
    // equal to) Menu's node, and the walk never reached this handler at
    // all (tickets/fable.md, 2026-08-14 diagnosis: every consumer shared
    // the gap — chat's channel rows and the tickets people pickers had
    // each grown their own local, per-opener workaround for exactly this,
    // see channelActions.js `ensureChatContextMenuEscClose` and
    // Sidebar.js `_focusContextMenuFirstItem`). Moving focus onto the
    // first row here — once, centrally — makes both of those redundant.
    // Also the MORE COMPLETE fix, not just an Escape patch: menuLevelNav's
    // own `idx = items.indexOf(document.activeElement)` already assumed a
    // row could hold focus.
    //
    // Mirrors the open/close focus-lifecycle idiom this same package
    // already ships for AppDialog (functions/appDialogSync.js +
    // appDialogFocusPanel.js) and Modal.js's own `syncUrl` — same
    // `el._wasOpen` / `el._returnFocusTo` names, same edge-detect-once
    // shape, same "a detached node's `.focus()` is a silent no-op, no
    // extra liveness check needed" reasoning. Edge-detected so it fires
    // EXACTLY ONCE per false->true (or true->false) transition:
    // `subscribeTo: ['contextDropdown']` reruns Menu's onUpdate on EVERY
    // contextDropdown mutation, including a submenu's `openKey` toggling
    // and an itemsFn recompute (e.g. a bound filter field) — neither may
    // yank focus back to the top level away from wherever the user
    // actually is. Every close path (Escape here, Backdrop click/
    // right-click, a terminal row action) converges on `contextDropdown:
    // null`, so the true->false edge is the one place that needs to
    // restore focus, regardless of which path fired.
    //
    // Bounded poll, NOT requestAnimationFrame — same reasoning as
    // ddSettle above (already rejected here: Chrome throttles frames in
    // backgrounded/automated tabs). `offsetWidth`/`offsetHeight > 0` is
    // the same "really rendered now, not still display:none behind the
    // parent's `hide:` gate" signal ddSettle already uses; the row query
    // reuses menuLevelNav's own `:scope >` idiom so a nested Submenu's
    // rows (two levels down, NOT a direct child of Menu) can never be
    // mistaken for the top level's first row.
    focusMenuOnOpen(el) {
      if (typeof window === 'undefined' || !el) return
      const dd = el.state?.root?.contextDropdown
      const isOpen = !!(dd && dd.open)
      if (isOpen === el._wasOpen) return
      el._wasOpen = isOpen
      if (!isOpen) {
        if (el._returnFocusTo && el._returnFocusTo.focus) el._returnFocusTo.focus()
        el._returnFocusTo = null
        return
      }
      el._returnFocusTo =
        document.activeElement && document.activeElement !== document.body
          ? document.activeElement
          : null
      el.__focusGen = (el.__focusGen || 0) + 1
      const gen = el.__focusGen
      let ticks = 0
      const tick = () => {
        if (gen !== el.__focusGen || !el.node) return
        if (el.node.offsetWidth > 0 && el.node.offsetHeight > 0) {
          const firstRow = el.node.querySelector(
            ':scope > [role="menuitem"], :scope > [role="menuitemcheckbox"]'
          )
          if (firstRow && firstRow.focus) firstRow.focus()
          else if (el.node.focus) el.node.focus()
          return
        }
        if (++ticks < 10) window.setTimeout(tick, 20)
      }
      tick()
    }
  },
  position: 'fixed',
  inset: '0',
  // zIndex is dynamic: an opener on an even-higher surface can pass
  // `contextDropdown.z` to lift the menu above it. The DEFAULT sits above the
  // shell's DropdownRoot layer (10010 — see app.js) and below ModalRoot
  // (10500): a context menu is the most transient surface on screen and must
  // never render underneath an open dropdown panel — the old 1400 default put
  // the right-click menu BEHIND the header-search results panel (2026-08-01
  // per Nika) and behind the messenger overlay (1900) for drawer person rows.
  // Per-key `style` so it re-applies reactively in v3.14 (a flat zIndex freezes
  // at mount).
  style: {
    zIndex: (el, s) => (s.root && s.root.contextDropdown && s.root.contextDropdown.z) || '10100'
  },
  hide: (el, s) => !(s.root && s.root.contextDropdown && s.root.contextDropdown.open),

  // Transparent backdrop — clicking (or right-clicking) anywhere off the menu
  // closes it. Sibling of the Menu, so item clicks don't hit it.
  Backdrop: {
    position: 'absolute',
    inset: '0',
    onPointerdown: (e, el, s) => s.root.update({ contextDropdown: null }, { preventFetch: true }),
    onContextmenu: (e, el, s) => {
      e.preventDefault()
      s.root.update({ contextDropdown: null }, { preventFetch: true })
    }
  },

  Menu: {
    extends: 'MenuPanel',
    position: 'absolute',
    // Programmatic focus target only — kept OUT of normal Tab order (the
    // standard ARIA menu-container idiom, same as AppDialog's Panel /
    // brand's ModalWindow). Lets `focusMenuOnOpen` fall back to focusing
    // the container itself when there's no row yet (an empty-items open,
    // or the poll below firing before a row exists), so Escape-close is
    // reachable via the delegated walk (see focusMenuOnOpen's comment)
    // even in that edge case.
    tabindex: '-1',
    // Viewport clamp shared by the reactive style factory AND the settle poll
    // so they never disagree (whoever writes inline `left`/`top` last would
    // otherwise win — that fight let the menu overflow). Until a real size is
    // measured, the height falls back to MenuPanel's 60vh cap (the tallest the
    // menu can ever be) — NOT a small guess — so even on the very first paint a
    // long list (e.g. the 50-row people picker) is clamped fully on-screen
    // instead of running off the bottom. ddSettle then measures the ACTUAL
    // rendered size onto `el._ddSize` and re-clamps, so short menus hug the
    // cursor. (2026-06-15 per Nika "dropdown items should never go out of
    // viewport"; settle poll 2026-07-03 per Nika "context menu on / does not
    // align at the bottom".)
    style: {
      left: (el, s) => el.scope.ddMenuPos(el, (s.root && s.root.contextDropdown) || {}).left + 'px',
      top: (el, s) => el.scope.ddMenuPos(el, (s.root && s.root.contextDropdown) || {}).top + 'px'
    },
    // Re-run the settle poll on mount AND on every contextDropdown change —
    // onRender alone runs before the rows lay out (measures 0×0, so the 60vh
    // worst-case clamp stuck: clicked near the bottom of /, the short menu sat
    // ~40vh above the cursor) and does not reliably re-fire per open in v3.14.
    // subscribeTo + onUpdate is the established re-arm pattern (CanvasPageSlot,
    // GridContainer).
    subscribeTo: ['contextDropdown'],
    onRender: (el) => {
      el.scope.ddSettle(el)
      el.scope.focusMenuOnOpen(el)
    },
    onUpdate: (el) => {
      el.scope.ddSettle(el)
      el.scope.focusMenuOnOpen(el)
    },
    // Arrow/Home/End move within the top level; Escape closes the WHOLE
    // menu. A row that owns an open submenu handles (and stops) its OWN
    // Escape/ArrowLeft first (see ContextDropdownItem.onKeydown) — this only
    // ever runs once nothing nested intercepted it, i.e. the true top level.
    onKeydown: (e, el, s) => {
      el.scope.menuLevelNav(e, el.node)
      if (e.key === 'Escape') {
        e.preventDefault()
        s.root && s.root.update({ contextDropdown: null }, { preventFetch: true })
      }
    },
    childExtends: 'ContextDropdownItem',
    childrenAs: 'state',
    children: (el, s) => {
      const dd = s.root && s.root.contextDropdown
      if (!dd) return []
      const list = dd.itemsFn ? el.call(dd.itemsFn, s.root) || [] : dd.items || []
      // Falsy holes (an itemsFn's `cond && {…}` conditional rows) must never
      // reach the reconciler — a stateless row runs every factory against
      // undefined, and one throw here kills this children effect for good.
      return (Array.isArray(list) ? list : []).filter((it) => it && typeof it === 'object')
    }
  }
}
